import { Injectable } from "@angular/core";
import * as Sentry from "@sentry/angular-ivy";

declare global {
  interface Window {
    voiceflow: {
      chat: {
        load: (args: any) => Promise<void>;
        open: () => void;
        close: () => void;
        show: () => void;
        hide: () => void;
        destroy: () => void;
        proactive: {
          clear: () => void;
          push: (...messages: ProactiveMessage[]) => void;
        };
      };
    };
  }
}

export type ProactiveMessage = {
  type: string;
  payload: {
    message: string;
  };
};

type UserInfo = {
  userID?: string;
  first_name: string;
  last_name: string;
};

type VoiceFlowConfig = {
  messages?: ProactiveMessage[];
  autostart?: boolean;
  assistant?: {
    renderMode?: "embed" | "widget" | "popover";
  };
};

const VOICEFLOW_SCRIPT_URL = "https://cdn.voiceflow.com/widget-next/bundle.mjs";
/** Max wait for the third-party script before treating load as failed. */
const SCRIPT_LOAD_TIMEOUT_MS = 30_000;

@Injectable({
  providedIn: "root",
})
export class VoiceflowService {
  private isVoiceflowScriptLoaded = false;
  private isVoiceflowChatLoaded = false;
  private isVoiceFlowLoading = false;
  private initializationPromise: Promise<void> | null = null;

  /**
   * Voiceflow's SDK sometimes rejects with a DOM {@link Event} (e.g. error on
   * &lt;link&gt; stylesheet) instead of an Error — normalize so Sentry and
   * Zone.js see a real Error.
   */
  private normalizeVoiceflowError(phase: string, error: unknown): Error {
    if (error instanceof Error) {
      return error;
    }
    if (error instanceof ErrorEvent) {
      const hint = this.describeEventTarget(error);
      const detail = [error.message, error.filename, error.lineno]
        .filter(Boolean)
        .join(" ");
      return new Error(
        `Voiceflow ${phase}: ${error.type}${detail ? ` ${detail}` : ""} (${hint})`
      );
    }
    if (error instanceof Event) {
      const hint = this.describeEventTarget(error);
      return new Error(
        `Voiceflow ${phase}: DOM ${error.type} on ${hint}`
      );
    }
    if (typeof error === "string") {
      return new Error(`Voiceflow ${phase}: ${error}`);
    }
    if (error === undefined || error === null) {
      return new Error(`Voiceflow ${phase}: rejected with ${error}`);
    }
    try {
      const s = JSON.stringify(error);
      const clipped = s.length > 400 ? `${s.slice(0, 400)}…` : s;
      return new Error(`Voiceflow ${phase}: ${clipped}`);
    } catch {
      return new Error(`Voiceflow ${phase}: non-Error rejection`);
    }
  }

  private describeEventTarget(event: Event): string {
    const t = event.target;
    if (t instanceof HTMLLinkElement) {
      return `link[href=${t.href || "?"}]`;
    }
    if (t instanceof HTMLScriptElement) {
      return `script[src=${t.src || "?"}]`;
    }
    if (t instanceof HTMLImageElement) {
      return `img[src=${t.src || "?"}]`;
    }
    if (t instanceof HTMLElement) {
      return t.tagName;
    }
    return t ? String(t) : "(no target)";
  }

  /**
   * Reports to Sentry at `warning` level and returns a normalized {@link Error}
   * to throw/reject so callers never surface raw DOM Events.
   */
  private voiceflowFailure(phase: string, error: unknown): Error {
    const err = this.normalizeVoiceflowError(phase, error);
    const extra: Record<string, unknown> = {};
    if (error instanceof ErrorEvent) {
      extra.dom_event_type = error.type;
      extra.dom_target = this.describeEventTarget(error);
      extra.error_event_message = error.message;
      extra.error_event_filename = error.filename;
      extra.isTrusted = error.isTrusted;
    } else if (error instanceof Event) {
      extra.dom_event_type = error.type;
      extra.dom_target = this.describeEventTarget(error);
      extra.isTrusted = error.isTrusted;
    } else if (!(error instanceof Error) && error !== undefined && error !== null) {
      extra.original_rejection = error;
    }

    try {
      Sentry.captureException(err, {
        level: "warning",
        tags: {
          feature: "voiceflow",
          voiceflow_phase: phase,
          ...(error instanceof ErrorEvent
            ? { voiceflow_rejection_kind: "error_event" }
            : error instanceof Event
              ? { voiceflow_rejection_kind: "dom_event" }
              : {}),
        },
        extra: Object.keys(extra).length ? extra : undefined,
      });
    } catch {
      // Never let telemetry break Voiceflow teardown paths (e.g. test doubles).
    }
    return err;
  }

  public async loadVoiceflowScript(): Promise<void> {
    if (this.isVoiceflowScriptLoaded) {
      return;
    }

    return new Promise<void>((resolve, reject) => {
      const v = document.createElement("script");
      let settled = false;

      let timeoutId = 0;

      const finish = (action: () => void) => {
        if (settled) {
          return;
        }
        settled = true;
        window.clearTimeout(timeoutId);
        v.onload = null;
        v.onerror = null;
        action();
      };

      timeoutId = window.setTimeout(() => {
        finish(() => {
          const err = new Error(
            `Voiceflow script load timed out after ${SCRIPT_LOAD_TIMEOUT_MS}ms`
          );
          reject(this.voiceflowFailure("script_timeout", err));
        });
      }, SCRIPT_LOAD_TIMEOUT_MS);

      v.onload = () => {
        finish(() => {
          this.isVoiceflowScriptLoaded = true;
          resolve();
        });
      };
      v.onerror = () => {
        finish(() => {
          reject(
            this.voiceflowFailure(
              "script_onerror",
              new Error("Voiceflow script failed to load")
            )
          );
        });
      };
      v.src = VOICEFLOW_SCRIPT_URL;
      v.type = "text/javascript";

      const firstScript = document.getElementsByTagName("script")[0];
      const parent = firstScript?.parentNode ?? document.head;
      parent.insertBefore(v, firstScript ?? null);
    });
  }

  public async loadVoiceChat(
    userInfo: UserInfo,
    config?: VoiceFlowConfig
  ): Promise<void> {
    if (!window.voiceflow?.chat) {
      throw this.voiceflowFailure(
        "chat_api_missing",
        new Error(
          "Voiceflow global is missing after script load (chat API unavailable)"
        )
      );
    }

    const { userID, first_name, last_name } = userInfo;
    const { messages, assistant, ...rest } = config ?? {};
    const renderMode = assistant?.renderMode ?? "widget";
    const stylesheet =
      renderMode === "popover"
        ? "/assets/chat-modal/popover-styles.css"
        : undefined;

    try {
      await window.voiceflow.chat.load({
        verify: { projectID: "657a6ffd384bd204ed1fa78b" },
        url: "https://general-runtime.voiceflow.com",
        versionID: "production",
        voice: {
          url: "https://runtime-api.voiceflow.com",
        },
        assistant: {
          persistence: "localStorage",
          renderMode,
          stylesheet,
          ...assistant,
        },
        userID,
        launch: {
          event: {
            type: "launch",
            payload: {
              first_name: first_name,
              last_name: last_name,
            },
          },
        },
        ...rest,
      });
    } catch (error) {
      throw this.voiceflowFailure("chat_load", error);
    }

    if (messages?.length) {
      try {
        window.voiceflow.chat.proactive.clear();
        window.voiceflow.chat.proactive.push(...messages);
      } catch (error) {
        throw this.voiceflowFailure("chat_proactive_messages", error);
      }
    }

    this.isVoiceflowChatLoaded = true;
  }

  public async openVoiceFlowChat(userInfo: UserInfo): Promise<void> {
    if (!this.isVoiceFlowInitialized) {
      await this.initializeVoiceFlow(userInfo);
    }

    this.showVoiceflowChat();
    window.voiceflow?.chat?.open();
  }

  public destroyVoiceFlow() {
    this.isVoiceflowChatLoaded = false;
    window.voiceflow?.chat?.destroy();
  }

  public hideVoiceflowChat() {
    window.voiceflow?.chat?.hide();
  }

  public showVoiceflowChat() {
    window.voiceflow?.chat?.show();
  }

  public get isVoiceFlowInitialized(): boolean {
    return this.isVoiceflowScriptLoaded && this.isVoiceflowChatLoaded;
  }

  public async initializeVoiceFlow(
    userInfo: UserInfo,
    config?: VoiceFlowConfig
  ) {
    if (this.isVoiceFlowLoading) {
      // If already loading, return the ongoing initialization promise
      return this.initializationPromise;
    }

    this.isVoiceFlowLoading = true;
    this.initializationPromise = (async () => {
      try {
        await this.loadVoiceflowScript();
        await this.loadVoiceChat(userInfo, config);
      } finally {
        this.isVoiceFlowLoading = false;
        this.initializationPromise = null;
      }
    })();

    return this.initializationPromise;
  }
}
